import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts';
import { EPSEQuarterData } from '@/types/epse';

interface EPSEDurationCurveProps {
  quarterData: EPSEQuarterData[];
  connectionLimit?: number;
}

/**
 * Hoeveel punten de grafiek maximaal tekent. Een duurcurve is monotoon dalend,
 * dus elk n-de punt overslaan verandert de vorm niet zichtbaar — bij 1.400 punten
 * op een grafiek van hooguit ~1.400 pixels breed is elk punt al minder dan een
 * pixel. De STATISTIEK eronder blijft wel over de volledige reeks lopen; dat is
 * het hele onderscheid, want een overschrijding die je wegdunt telt niet meer mee.
 */
const MAX_PUNTEN = 1400;

export function EPSEDurationCurve({ quarterData, connectionLimit }: EPSEDurationCurveProps) {
  // Tot 08-09-2026 kreeg dit component 1.000 kwartieren, want useEPSEQuarterData
  // kapte stil af op het PostgREST-plafond. Nu komt de volledige reeks binnen —
  // 35.175 kwartieren voor een heel jaar. Zonder useMemo betekende dat kopiëren,
  // sorteren en 35.175 x 3 strings bouwen bij ELKE render.
  const { chartData, exceedanceCount, totaal } = useMemo(() => {
    const gesorteerd = quarterData
      .map(q => q.kw_value)
      .sort((a, b) => b - a);

    const n = gesorteerd.length;
    const stap = Math.max(1, Math.ceil(n / MAX_PUNTEN));

    const punten: { index: number; hours: string; percent: string; kw: number }[] = [];
    for (let i = 0; i < n; i += stap) {
      punten.push({
        index: i,
        hours: (i * 0.25).toFixed(1),
        percent: ((i / n) * 100).toFixed(1),
        kw: gesorteerd[i],
      });
    }
    // Het laatste punt hoort er altijd bij, anders stopt de curve vlak voor het
    // einde en lijkt het minimum hoger dan het is.
    if (n > 0 && punten[punten.length - 1].index !== n - 1) {
      punten.push({
        index: n - 1,
        hours: ((n - 1) * 0.25).toFixed(1),
        percent: '100.0',
        kw: gesorteerd[n - 1],
      });
    }

    // Over de VOLLEDIGE reeks, niet over de uitgedunde punten.
    const boven = connectionLimit
      ? gesorteerd.filter(v => v > connectionLimit).length
      : 0;

    return { chartData: punten, exceedanceCount: boven, totaal: n };
  }, [quarterData, connectionLimit]);

  const exceedanceHours = exceedanceCount * 0.25;
  const exceedancePercent = totaal > 0 ? (exceedanceCount / totaal) * 100 : 0;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Duurcurve</CardTitle>
        <CardDescription>
          Gesorteerd vermogen van hoog naar laag
          {connectionLimit && exceedanceCount > 0 && (
            <span className="text-destructive ml-2">
              • {exceedanceHours.toFixed(1)} uur boven limiet ({exceedancePercent.toFixed(1)}%)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="percent"
                tick={{ fontSize: 10 }}
                tickFormatter={(v) => `${v}%`}
                label={{ value: 'Tijd (%)', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                tickFormatter={(v) => `${v} kW`}
              />
              <Tooltip 
                formatter={(value: number) => [`${value.toFixed(1)} kW`, 'Vermogen']}
                labelFormatter={(label) => `${label}% van de tijd`}
              />
              
              {connectionLimit && (
                <ReferenceLine 
                  y={connectionLimit} 
                  stroke="hsl(var(--destructive))" 
                  strokeDasharray="5 5"
                  label={{ 
                    value: `Limiet: ${connectionLimit} kW`, 
                    position: 'right',
                    fontSize: 10,
                  }}
                />
              )}
              
              <defs>
                <linearGradient id="durationGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              
              <Area 
                type="monotone" 
                dataKey="kw" 
                stroke="hsl(var(--primary))" 
                fill="url(#durationGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
